// ==========================================================
// Journal.js — Updated with clean toast, fade-in animations,
// and Custom Mood Picker (Option A)
// ==========================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { db } from "../firebaseConfig";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import useAuth from "../hooks/useAuth";

/* ---------- Helper Functions ---------- */
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const toKey = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const fromTimestampOrDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  try {
    return new Date(ts);
  } catch {
    return null;
  }
};

const monthLabel = (d) =>
  d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

const weekdayShort = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function buildMonthGrid(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const startOffset = first.getDay();
  const start = new Date(first);
  start.setDate(first.getDate() - startOffset);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

/* ==========================================================
   Component
=========================================================== */
export default function Journal() {
  const { user } = useAuth();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [mood, setMood] = useState("😊");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [monthDate, setMonthDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDateKey, setSelectedDateKey] = useState(() =>
    toKey(new Date())
  );

  const [showToast, setShowToast] = useState(false);

  // For auto-scroll
  const entriesTopRef = useRef(null);

  /* ---------------- CUSTOM MOODS (Option A) ---------------- */
  const defaultMoods = ["😊", "😐", "😢", "😡", "😴", "🤩"];

  const savedCustomMoods = JSON.parse(
    localStorage.getItem("rr_custom_moods") || "[]"
  );

  const [customMoods, setCustomMoods] = useState(savedCustomMoods);

  const addCustomMood = () => {
    const emoji = prompt("Enter a mood emoji (e.g. 😊):");

    if (!emoji) return;
    if (emoji.length > 4) return alert("Please enter a valid emoji.");

    const updated = [...customMoods, emoji];
    setCustomMoods(updated);
    localStorage.setItem("rr_custom_moods", JSON.stringify(updated));
  };

  /* ---------- Firestore Sync ---------- */
  useEffect(() => {
    if (!user) return;
    setLoading(true);

    const q = query(
      collection(db, "journal"),
      where("uid", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEntries(docs);
        setLoading(false);
      },
      (err) => {
        console.error("Journal subscribe error:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user]);

  const entriesByDay = useMemo(() => {
    const map = {};
    for (const e of entries) {
      const created = fromTimestampOrDate(e.createdAt) || new Date();
      const key = toKey(created);
      if (!map[key]) map[key] = [];
      map[key].push(e);
    }
    return map;
  }, [entries]);

  const selectedEntries = useMemo(
    () =>
      (entriesByDay[selectedDateKey] || []).slice().sort((a, b) => {
        const da = fromTimestampOrDate(a.createdAt)?.getTime() || 0;
        const db = fromTimestampOrDate(b.createdAt)?.getTime() || 0;
        return db - da;
      }),
    [entriesByDay, selectedDateKey]
  );

  /* ---------- Add Entry ---------- */
  const addEntry = async () => {
    if (!note.trim() || !user) return;

    try {
      await addDoc(collection(db, "journal"), {
        uid: user.uid,
        text: note.trim(),
        mood,
        createdAt: serverTimestamp(),
      });

      setNote("");
      setMood("😊");
      setSelectedDateKey(toKey(new Date()));

      // Show toast
      setShowToast(true);
      setTimeout(() => setShowToast(false), 1800);

      // Auto scroll
      setTimeout(() => {
        if (entriesTopRef.current) {
          entriesTopRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    } catch (err) {
      console.error("Error adding journal entry:", err);
      alert("Could not add entry. Please try again.");
    }
  };

  /* ---------- Editing ---------- */
  const startEditing = (entry) => {
    setEditingId(entry.id);
    setEditText(entry.text || "");
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    try {
      await updateDoc(doc(db, "journal", id), { text: editText.trim() });
      setEditingId(null);
      setEditText("");
    } catch (err) {
      console.error("Update failed:", err);
      alert("Could not update entry.");
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await deleteDoc(doc(db, "journal", id));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Could not delete entry.");
    }
  };

  /* ---------- Calendar ---------- */
  const prevMonth = () => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() - 1);
    setMonthDate(d);
  };

  const nextMonth = () => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() + 1);
    setMonthDate(d);
  };

  const cells = useMemo(() => buildMonthGrid(monthDate), [monthDate]);
  const thisMonth = monthDate.getMonth();
  const todayKey = toKey(new Date());

  /* ---------- UI ---------- */
  if (!user) return <div className="card">Please sign in to view your journal.</div>;
  if (loading) return <div className="card">Loading your journal…</div>;

  return (
    <>
      <Helmet>
        <title>Journal – ReflectRise</title>
      </Helmet>

      {/* TOAST */}
      {showToast && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--primary)",
            color: "#fff",
            padding: "10px 18px",
            borderRadius: "8px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
            zIndex: 999,
            opacity: 1,
            transition: "opacity 0.4s ease",
          }}
        >
          Entry added
        </div>
      )}

      <div
        className="journal-page"
        style={{
          background: "var(--bg)",
          color: "var(--text)",
          minHeight: "100vh",
          padding: "2rem 1rem",
        }}
      >
        {/* HEADER */}
        <div className="journal-header" style={{ textAlign: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ color: "var(--primary)" }}>🪶 Journal</h2>
          <p style={{ color: "var(--muted)" }}>Capture your thoughts, moods, and reflections.</p>
        </div>

        {/* ADD ENTRY CARD */}
        <div
          className="journal-input-section"
          style={{
            background: "var(--card)",
            padding: "1rem",
            borderRadius: "12px",
            boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
            marginBottom: "2rem",
            maxWidth: "700px",
            margin: "0 auto",
          }}
        >
          {/* Mood Picker */}
          <div
            className="mood-picker"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "14px",
            }}
          >
            {[...defaultMoods, ...customMoods].map((m) => (
              <button
                key={m}
                className="mood-btn"
                onClick={() => setMood(m)}
                style={{
                  fontSize: "1.5rem",
                  background: mood === m ? "var(--primary)" : "var(--card)",
                  color: mood === m ? "#fff" : "var(--text)",
                  borderRadius: "50%",
                  padding: "6px 12px",
                  border: "none",
                  cursor: "pointer",
                  transition: "0.15s",
                }}
              >
                {m}
              </button>
            ))}

            {/* Add custom emoji */}
            <button
              onClick={addCustomMood}
              style={{
                fontSize: "1.3rem",
                background: "var(--card)",
                color: "var(--primary)",
                borderRadius: "50%",
                padding: "6px 12px",
                border: "1px solid var(--primary)",
                cursor: "pointer",
              }}
            >
              +
            </button>
          </div>

          <div className="journal-input-wrapper">
            <input
              className="input"
              placeholder="Write a quick thought..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addEntry()}
              style={{
                flex: "1",
                borderRadius: "8px",
                padding: "10px",
                color: "var(--text)",
                background: "var(--bg)",
              }}
            />

            <button className="btn" onClick={addEntry}>
              Add
            </button>
          </div>
        </div>

        {/* CALENDAR */}
        <div
          className="calendar-container"
          style={{
            background: "var(--card)",
            padding: "1rem",
            borderRadius: "12px",
            margin: "1.5rem auto",
            maxWidth: "700px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
          }}
        >
          <div className="calendar-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button className="cal-btn" onClick={prevMonth}>‹</button>
            <h3 style={{ color: "var(--primary)" }}>{monthLabel(monthDate)}</h3>
            <button className="cal-btn" onClick={nextMonth}>›</button>
          </div>

          <div className="calendar-grid">
            {weekdayShort.map((w) => (
              <div key={w} className="calendar-weekday">{w}</div>
            ))}

            {cells.map((d) => {
              const key = toKey(d);
              const inMonth = d.getMonth() === thisMonth;
              const isToday = key === todayKey;
              const isSelected = key === selectedDateKey;
              const count = entriesByDay[key]?.length || 0;

              return (
                <button
                  key={key}
                  onClick={() => setSelectedDateKey(key)}
                  className={[
                    "calendar-cell",
                    inMonth ? "" : "muted",
                    isToday ? "today" : "",
                    isSelected ? "selected" : "",
                  ].join(" ")}
                  style={{
                    borderRadius: "8px",
                    padding: "6px 0",
                    color: inMonth ? "var(--text)" : "var(--muted)",
                    background: isSelected
                      ? "var(--primary)"
                      : isToday
                      ? "var(--primary-2)"
                      : "transparent",
                    transition: "0.2s",
                  }}
                >
                  {d.getDate()}
                  {count > 0 && (
                    <div
                      style={{
                        width: "5px",
                        height: "5px",
                        borderRadius: "50%",
                        background: "var(--text)",
                        margin: "3px auto 0",
                      }}
                    ></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ENTRIES LIST */}
        <div
          ref={entriesTopRef}
          className="entries-section"
          style={{
            maxWidth: "700px",
            margin: "2rem auto",
            background: "var(--card)",
            padding: "1rem",
            borderRadius: "12px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
          }}
        >
          <h3 style={{ color: "var(--primary)", textAlign: "center" }}>
            {new Date(selectedDateKey).toLocaleDateString(undefined, {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </h3>

          {selectedEntries.length === 0 ? (
            <p style={{ color: "var(--muted)", textAlign: "center", marginTop: "1rem" }}>
              No entries for this day.
            </p>
          ) : (
            selectedEntries.map((entry) => {
              const when = fromTimestampOrDate(entry.createdAt);
              const timeLabel = when
                ? when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                : "";

              return (
                <div
                  key={entry.id}
                  className="entry-item"
                  style={{
                    animation: "fadeIn 0.4s ease",
                    borderLeft: "3px solid var(--primary)",
                    background: "var(--bg)",
                    color: "var(--text)",
                    borderRadius: "10px",
                    marginTop: "10px",
                    padding: "12px",
                  }}
                >
                  <style>
                    {`
                    @keyframes fadeIn {
                      from { opacity: 0; transform: translateY(6px); }
                      to { opacity: 1; transform: translateY(0); }
                    }
                    `}
                  </style>

                  <div className="entry-top" style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{entry.mood}</span>
                    <span style={{ color: "var(--muted)" }}>{timeLabel}</span>
                  </div>

                  {editingId === entry.id ? (
                    <div>
                      <textarea
                        className="input"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        style={{
                          width: "100%",
                          marginTop: "8px",
                          background: "var(--card)",
                          color: "var(--text)",
                          borderRadius: "8px",
                          padding: "8px",
                        }}
                      />
                      <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                        <button className="btn" onClick={() => saveEdit(entry.id)}>
                          Save
                        </button>
                        <button className="btn secondary" onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p style={{ marginTop: "10px" }}>{entry.text}</p>
                  )}

                  <div className="entry-actions" style={{ marginTop: "10px" }}>
                    {editingId !== entry.id && (
                      <button
                        className="btn"
                        style={{ marginRight: "8px" }}
                        onClick={() => startEditing(entry)}
                      >
                        Edit
                      </button>
                    )}
                    <button className="btn secondary" onClick={() => deleteEntry(entry.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
